import { clampViewportOffset } from '@/utils/clampViewportOffset';
import {
    createTouchZoomState,
    handleTouchEnd,
    handleTwoFingerTouch,
    validateZoomState,
} from '@/infrastructure/engine2d/input/touchZoomState';
import { Point, type Canvas, type TPointerEvent, type TPointerEventInfo } from 'fabric';
import type { IFabricImageCustom } from '@/infrastructure/engine2d/types/IFabricImageCustom';
import type { IFabricTextCustom } from '@/infrastructure/engine2d/types/IFabricTextCustom';
import type { ITextboxCustom } from '@/infrastructure/engine2d/types/ITextboxCustom';
import type { TViewportConstraints } from '@/utils/TViewportConstraints';

//#region Initialization & Setup

function selectTextWord(ev: TPointerEventInfo, fCanvas: Canvas) {
    const scenePointer = fCanvas.getScenePoint(ev.e);
    const fCanvasObj = fCanvas.getObjects().find((obj) => obj.selectable && obj.containsPoint(scenePointer));

    if (fCanvasObj?.type === 'textbox') {
        const textbox = fCanvasObj as ITextboxCustom;
        fCanvas.setActiveObject(textbox);
        textbox.enterEditing();
        let charIndex = -1;

        if (typeof textbox.getSelectionStartFromPointer === 'function') {
            charIndex = textbox.getSelectionStartFromPointer(ev.e as MouseEvent);
        }

        if (charIndex === -1) {
            // Get text bounds and calculate approximate character position
            const textBounds = textbox.getBoundingRect();
            const relativeX = scenePointer.x - textBounds.left;

            // Simple approximation: assume average character width
            const text = textbox.text || '';
            const avgCharWidth = textBounds.width / Math.max(text.length, 1);
            const estimatedCharIndex = Math.floor(relativeX / avgCharWidth);

            charIndex = Math.max(0, Math.min(estimatedCharIndex, text.length - 1));
        }

        if (charIndex === -1 || isNaN(charIndex)) {
            charIndex = textbox.selectionStart || 0;
        }
        textbox.selectWord(charIndex);
    }
}

// Initialize click callback for pan operations. Returns a callback
// wired into mouse:down / mouse:up in PanController.
function initClickCallback(
    fabricCanvas: Canvas,
    refPointSetterFunction: (point: Point | null) => void,
    setClickedActiveObject: (object: IFabricImageCustom | IFabricTextCustom | ITextboxCustom | undefined) => void,
): (opt: TPointerEventInfo) => void {
    let isDblClick = false;
    let timeout: NodeJS.Timeout | undefined;
    let lastTouchTime = 0;
    return (opt: TPointerEventInfo) => {
        const activeObject = fabricCanvas.getActiveObject();
        if (opt.e.type === 'touchend') {
            clearTimeout(timeout); // Clear any previous timeout
            if (isDblClick) {
                // Handle double-click logic here if needed
                isDblClick = false; // Reset for next touch
                selectTextWord(opt, fabricCanvas);
                opt.e.preventDefault();
                opt.e.stopPropagation();
                fabricCanvas.renderAll();
                return; // Exit to prevent single click logic
            }
            isDblClick = true; // Reset if already set
            timeout = setTimeout(() => {
                isDblClick = false;
            }, 300); // Reset after 300ms
        }
        if (['mousedown', 'touchstart'].includes(opt.e.type)) {
            const currentTime = new Date().getTime();
            if (currentTime - lastTouchTime < 300 && opt.e.type === 'touchstart') {
                // Double touch detected
                selectTextWord(opt, fabricCanvas);
                fabricCanvas.renderAll();
                lastTouchTime = 0;
            }
            if (activeObject) {
                setClickedActiveObject(activeObject as IFabricImageCustom | IFabricTextCustom | ITextboxCustom | undefined);
            } else {
                refPointSetterFunction(opt.viewportPoint);
                setClickedActiveObject(undefined);
            }
            if (opt.e.type === 'touchstart') {
                lastTouchTime = currentTime;
            }
        }
        if (['mouseup', 'touchend'].includes(opt.e.type)) {
            refPointSetterFunction(null);
        }
    };
}

// Initialize pan callback wired into mouse:move in PanController.
// Handles single-touch / mouse pan and dispatches two-finger touch to
// touchZoomState.
function initPanCallback(
    fabricCanvas: Canvas,
    clickedRefPointGetter: () => Point | null,
    refPointSetterFunction: (point: Point | null) => void,
    notifyZoom: (zoom: number) => void,
    getConstraints: () => TViewportConstraints,
): (opt: TPointerEventInfo) => void {
    const touchZoomState = createTouchZoomState();

    return (opt: TPointerEventInfo) => {
        const { e }: { e: TPointerEvent } = opt;

        // Validate and reset zoom state if needed
        validateZoomState(e, touchZoomState);

        // Handle two-finger touch gestures (zoom + pan)
        if (window.TouchEvent && e instanceof window.TouchEvent && e.touches.length === 2) {
            handleTwoFingerTouch(e, touchZoomState, fabricCanvas, getConstraints(), notifyZoom);
            return;
        }

        // Handle touch end/cancel events
        if (window.TouchEvent && e instanceof window.TouchEvent && handleTouchEnd(e, touchZoomState)) {
            return;
        }

        // Skip regular pan behavior if we're in a zoom gesture
        if (touchZoomState.isZooming) {
            return;
        }

        // Handle regular pan behavior for single touch or mouse
        handleRegularPan(opt, fabricCanvas, clickedRefPointGetter, refPointSetterFunction, getConstraints());
    };
}

//#endregion

//#region Pan Operations

/**
 * Handles regular pan behavior for single touch or mouse
 */
function handleRegularPan(
    opt: TPointerEventInfo,
    fabricCanvas: Canvas,
    clickedRefPointGetter: () => Point | null,
    refPointSetterFunction: (point: Point | null) => void,
    constraints: TViewportConstraints,
): void {
    const point = clickedRefPointGetter();
    if (!point) {
        return;
    }

    // Prevent default behavior for all pointer events during panning
    opt.e.preventDefault();
    opt.e.stopPropagation();

    const { x: Vx, y: Vy } = opt.viewportPoint;
    const { x: Cx, y: Cy } = point;
    const { x: Ox, y: Oy } = new Point(Vx - Cx, Vy - Cy); // new offset vector calculation
    const [matX, matY, matZ, matW, offsetX, offsetY] = fabricCanvas.viewportTransform;

    const canvasEl = fabricCanvas.getElement();
    // The content is the print surface, not the canvas element: using
    // fabricCanvas.width here made the allowed range shrink as the user zoomed
    // in, so the clamp fought the pan instead of bounding it.
    const clamped = clampViewportOffset({
        contentHeight: constraints.contentHeight,
        contentWidth: constraints.contentWidth,
        offsetX: offsetX + Ox,
        offsetY: offsetY + Oy,
        viewportHeight: canvasEl.clientHeight || fabricCanvas.height,
        viewportWidth: canvasEl.clientWidth || fabricCanvas.width,
        zoom: fabricCanvas.getZoom(),
    });

    fabricCanvas.setViewportTransform([matX, matY, matZ, matW, clamped.x, clamped.y]);
    refPointSetterFunction(opt.viewportPoint);
}

/**
 * Applies pan transformation to the canvas
 */
function applyPanTransformation(fabricCanvas: Canvas, panDelta: Point): void {
    // Only pan if movement is significant
    if (Math.abs(panDelta.x) > 2 || Math.abs(panDelta.y) > 2) {
        const [matX, matY, matZ, matW, offsetX, offsetY] = fabricCanvas.viewportTransform;
        const newOffsetX = offsetX + panDelta.x;
        const newOffsetY = offsetY + panDelta.y;
        fabricCanvas.setViewportTransform([matX, matY, matZ, matW, newOffsetX, newOffsetY]);
    }
}

//#endregion

// Export only functions used by external modules
export { applyPanTransformation, initClickCallback, initPanCallback };
